/**
 * The New Connection dialog's start ↔ rail transition (Option C, C1b, plan §3.2; the prototype's `flipItems` /
 * `crossfadeViews`). A shared-element FLIP on the Web Animations API, no library. React unmounts the view being left at
 * commit; a static, inert copy of it (`flipOutgoing.ts`) fades out over the incoming view, so no frame is empty and
 * nothing invisible takes a click. Nothing blocks input during a flight: the flip layers stop taking pointers
 * (`data-flip-busy`), so a click lands on the real control under it.
 *
 * - `capture(to)` — the navigation calls it for EVERY view change, before its state update (`useDialogNavigation`'s
 *   `showView`). It reads the motion mode (never cached), copies the outgoing view and, for the two-pane FLIP, the First
 *   boxes.
 * - `captureSwap(engine)` — an engine switch in the form view (rail, compact select): copies the pane, which then
 *   cross-fades with the new one.
 * - A captured flight is consumed by the view change it was captured for — never by an engine-only commit in between
 *   (a pick landing after an await commits the engine first; C3.1).
 * - After the commit (a layout effect declared after the navigation's focus effect, so focus has already moved) the
 *   flight plays: the Last scene is read in one pass, the previous flight is cancelled, every `animate()` is issued.
 *   Reduced motion cross-fades; a narrow window slides the incoming page (transitions-dev 08).
 * - `settle()` ends a flight at once (`flipController.ts`): the navigation calls it before a filter change or the start
 *   paste field opening; this hook calls it on a resize, when focus leaves the dialog (a nested dialog or menu opening)
 *   and when the reduced-motion preference changes. Unmount cancels everything.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSystemPrefersReducedMotion } from '@/components/ui/dotmatrix-hooks';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import type { DialogView } from '../state/dialogView';
import { createFlipController, type FlipController } from './flipController';
import { beginFlight, clearFlight, FLIPPING_ATTRIBUTE, pagesOf, playSpecs, readRailSwitch, readScene, snapshotLayers } from './flipDom';
import { captureOutgoing, insertOutgoing, type OutgoingView } from './flipOutgoing';
import { layerId, planOutgoingFade, planPageSwap, planRailSwitch, planSharedFlip, type AnimationSpec, type FlightTarget, type LayerState } from './flipPlan';
import { motionMode } from './motionPolicy';
import { readMotionTokens } from './motionTokens';

type ViewKind = DialogView['kind'];
type Mode = 'full' | 'reduced';

type Flight = { to: FlightTarget; mode: Mode; first: Map<string, LayerState> | null; outgoing: OutgoingView[] };
type Swap = { to: DatabaseEngine; outgoing: OutgoingView[] };

export type DialogMotion = {
  /** Before a view change's state update: copy the view being left and measure where the layers are now. */
  capture: (to: ViewKind) => void;
  /** Before an engine switch in the form view: copy the pane, to cross-fade it with the new one. */
  captureSwap: (to: DatabaseEngine) => void;
  /** End any running flight at once (before a layout change that is not a navigation). */
  settle: () => void;
};

export const engineFlipKey = (engine: DatabaseEngine) => `engine:${engine}`;

const tokens = () => readMotionTokens(typeof document === 'undefined' ? null : getComputedStyle(document.documentElement));

function copies(root: HTMLElement, elements: readonly HTMLElement[]): OutgoingView[] {
  return elements.map((element) => captureOutgoing(root, element)).filter((view): view is OutgoingView => view !== null);
}

function captureFlight(root: HTMLElement | null, to: ViewKind): Flight | null {
  const mode = motionMode();
  if (!root || mode === 'off') return null;
  const twoPane = root.getAttribute('data-layout') === 'two-pane';
  return { to, mode, first: mode === 'full' && twoPane ? snapshotLayers(root) : null, outgoing: copies(root, pagesOf(root)) };
}

/** Puts the outgoing copies back (the flying items hidden in them) and plans their fade-out. */
function outgoingSpecs(root: HTMLElement, outgoing: readonly OutgoingView[], flyingKeys: ReadonlySet<string>, mode: Mode): AnimationSpec<HTMLElement>[] {
  const inserted = outgoing.map((view) => ({ target: insertOutgoing(root, view, flyingKeys), opacity: view.opacity }));
  return planOutgoingFade(inserted, mode, tokens());
}

function playFlight(root: HTMLElement, flight: Flight, controller: FlipController): void {
  controller.begin();
  const first = flight.first;
  beginFlight(root, { flipping: first !== null });
  if (!first) {
    const incoming = planPageSwap(pagesOf(root), flight.to, flight.mode, tokens());
    playSpecs([...outgoingSpecs(root, flight.outgoing, new Set(), flight.mode), ...incoming], controller);
    return;
  }
  // The Last scene is read before the copies go in (they carry no flip hooks, and they are not the new view).
  const scene = readScene(root, first);
  const flying = new Set(scene.layers.filter((layer) => first.has(layerId(layer.key, layer.name))).map((layer) => layer.key));
  const incoming = planSharedFlip(scene, flight.to, tokens());
  playSpecs([...outgoingSpecs(root, flight.outgoing, flying, flight.mode), ...incoming], controller);
}

function playEngineSwitch(root: HTMLElement, from: DatabaseEngine, to: DatabaseEngine, swap: Swap | null, controller: FlipController): void {
  const mode = motionMode();
  if (mode === 'off') return;
  const scene = readRailSwitch(root, engineFlipKey(from), engineFlipKey(to));
  const outgoing = swap?.to === to ? swap.outgoing : [];
  if (root.hasAttribute(FLIPPING_ATTRIBUTE)) {
    // The rail keeps moving; the two items' surfaces take their new rest opacity at once.
    const keys = [engineFlipKey(from), engineFlipKey(to)];
    controller.cancelWhere((meta) => meta.layer === 'surface' && meta.props.includes('opacity') && keys.includes(meta.key ?? ''));
    playSpecs([...outgoingSpecs(root, outgoing, new Set(), mode), ...planRailSwitch({ pane: scene.pane, highlight: null }, mode, tokens())], controller);
    return;
  }
  controller.begin();
  beginFlight(root, { flipping: false });
  playSpecs([...outgoingSpecs(root, outgoing, new Set(), mode), ...planRailSwitch(scene, mode, tokens())], controller);
}

export function useSharedLayoutTransition({ root, view, engine }: { root: HTMLElement | null; view: ViewKind; engine: DatabaseEngine }): DialogMotion {
  const rootRef = useRef(root);
  rootRef.current = root;
  const [controller] = useState(() => createFlipController(() => clearFlight(rootRef.current)));
  const pending = useRef<Flight | null>(null);
  const pendingSwap = useRef<Swap | null>(null);
  const previous = useRef({ view, engine });

  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = { view, engine };
    const element = rootRef.current;
    if (before.view !== view) {
      const flight = pending.current;
      pending.current = null;
      pendingSwap.current = null;
      // A view change nobody captured (a save that returns to the start view) is instant.
      if (element && flight?.to === view) playFlight(element, flight, controller);
      else controller.settle();
    } else if (before.engine !== engine) {
      // A captured flight survives an engine-only commit: a Browse Azure pick from the start view lands after an awaited
      // identity check, so the draft's store update (a sync external store) commits the engine BEFORE the view change it
      // was captured for (C3.1). Only the pane swap belongs to this commit.
      const swap = pendingSwap.current;
      pendingSwap.current = null;
      if (element && view === 'form') playEngineSwitch(element, before.engine, engine, swap, controller);
    }
  }, [view, engine, controller]);

  useEffect(() => {
    if (!root) return;
    const settle = () => controller.settle();
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !root.contains(next)) settle();
    };
    root.addEventListener('focusout', onFocusOut);
    window.addEventListener('resize', settle);
    return () => {
      root.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('resize', settle);
    };
  }, [root, controller]);

  const reducedMotion = useSystemPrefersReducedMotion();
  useEffect(() => {
    controller.settle();
  }, [reducedMotion, controller]);

  useEffect(() => () => controller.cancelAll(), [controller]);

  return useMemo(
    () => ({
      capture: (to: ViewKind) => {
        pending.current = captureFlight(rootRef.current, to);
      },
      captureSwap: (to: DatabaseEngine) => {
        const element = rootRef.current;
        const pane = element?.querySelector<HTMLElement>('[data-flip-chrome="pane"]');
        pendingSwap.current = element && pane && motionMode() !== 'off' ? { to, outgoing: copies(element, [pane]) } : null;
      },
      settle: () => controller.settle(),
    }),
    [controller],
  );
}
